import { EmailPageLayout } from "@/components/email/EmailPageLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  FileText,
  Info,
  Mail,
  Shield,
  UserCheck,
  Settings,
  AlertCircle,
  BookOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { motion } from "framer-motion";

export default function EmailTerms() {
  return (
    <EmailPageLayout
      title="Email Terms of Service"
      description="Terms governing email communications with Numberz Insight"
    >
      <div className="relative bg-gradient-to-b from-primary/10 to-background py-8 overflow-hidden rounded-t-lg">
        <div className="absolute inset-0 z-0 opacity-30">
          <img
            src="https://images.unsplash.com/photo-1596526131083-e8c633c948d2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80"
            alt="Email terms background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-background"></div>
        </div>
        <div className="relative z-10 px-4 max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h1 className="text-3xl font-bold mb-2">Email Terms of Service</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Please read these terms carefully as they govern all email
              communications
            </p>
            <div className="flex items-center justify-center mt-4 text-sm text-muted-foreground">
              <Mail className="h-4 w-4 mr-2" />
              <span>
                Last updated:{" "}
                {new Date().toLocaleDateString("en-AU", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="px-4 py-6 max-w-3xl mx-auto w-full">
        <Tabs defaultValue="general" className="w-full">
          <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-sm pt-4 pb-2 mb-6">
            <TabsList className="w-full justify-start overflow-x-auto flex p-1 rounded-lg bg-muted/50">
              <TabsTrigger
                value="general"
                className="rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <Info className="h-4 w-4 mr-2" />
                General Terms
              </TabsTrigger>
              <TabsTrigger
                value="communications"
                className="rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <Mail className="h-4 w-4 mr-2" />
                Communications
              </TabsTrigger>
              <TabsTrigger
                value="security"
                className="rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <Shield className="h-4 w-4 mr-2" />
                Security & Privacy
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="general" className="space-y-6">
            <Alert className="bg-primary/5 border-primary/20">
              <Info className="h-4 w-4 text-primary" />
              <AlertTitle>About Our Email Terms</AlertTitle>
              <AlertDescription>
                These terms establish how we communicate with you via email and
                your rights regarding these communications.
              </AlertDescription>
            </Alert>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <BookOpen className="h-5 w-5 mr-2 text-primary" />
                  Introduction
                </CardTitle>
                <CardDescription>
                  The legal framework for our email communications
                </CardDescription>
              </CardHeader>
              <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                <p>
                  These Email Terms of Service ("Terms") govern all email
                  communications between you and Numberz Insight Pty Ltd ("we,"
                  "our," or "us"). By providing your email address and engaging
                  with our services, you agree to these Terms.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <Settings className="h-5 w-5 mr-2 text-primary" />
                  Consent and Opt-Out
                </CardTitle>
              </CardHeader>
              <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                <p>
                  By providing your email address during registration or service
                  engagement, you consent to receive emails related to your tax
                  services. You can manage your email preferences or opt out of
                  non-essential communications at any time through our Email
                  Preferences Center or by clicking the unsubscribe link in any
                  email.
                </p>
                <p>
                  Please note that even if you opt out of marketing
                  communications, we may still send you essential emails related
                  to your account, security, or legal matters.
                </p>
                <div className="mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      (window.location.href = "/email/preferences")
                    }
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Manage Email Preferences
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <AlertCircle className="h-5 w-5 mr-2 text-primary" />
                  Changes & Governing Law
                </CardTitle>
              </CardHeader>
              <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                <h3>Changes to These Terms</h3>
                <p>
                  We may update these Terms from time to time. We will notify
                  you of any material changes via email or by posting a notice
                  on our website. Your continued use of our services after such
                  notification constitutes acceptance of the updated Terms.
                </p>

                <h3>Governing Law</h3>
                <p>
                  These Terms are governed by the laws of Victoria, Australia,
                  without regard to its conflict of law provisions.
                </p>

                <h3>Contact Information</h3>
                <p>
                  If you have any questions about these Email Terms of Service,
                  please contact us at:
                </p>
                <ul>
                  <li>Email: support@numberz.au</li>
                  <li>Phone: 1300 123 435</li>
                  <li>Mail: Unit 41/2 Fastline Rd, Truganina VIC 3029</li>
                </ul>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="communications" className="space-y-6">
            <Alert className="bg-primary/5 border-primary/20">
              <Mail className="h-4 w-4 text-primary" />
              <AlertTitle>Email Communications</AlertTitle>
              <AlertDescription>
                Information about the types of emails we send and how we manage
                them.
              </AlertDescription>
            </Alert>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <Mail className="h-5 w-5 mr-2 text-primary" />
                  Types of Email Communications
                </CardTitle>
              </CardHeader>
              <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                <p>We may send you the following types of emails:</p>
                <ul>
                  <li>
                    <strong>Document Requests:</strong> Emails requesting
                    specific tax documents from you to complete your tax return.
                  </li>
                  <li>
                    <strong>Information Requests:</strong> Emails requesting
                    additional information to clarify tax matters.
                  </li>
                  <li>
                    <strong>Service Updates:</strong> Emails providing updates
                    about your tax service progress.
                  </li>
                  <li>
                    <strong>Marketing Communications:</strong> Occasional emails
                    about new services or features (only sent with explicit
                    consent).
                  </li>
                  <li>
                    <strong>Account Notifications:</strong> Essential emails
                    regarding your account, security, or legal matters.
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <FileText className="h-5 w-5 mr-2 text-primary" />
                  Email Content and Frequency
                </CardTitle>
              </CardHeader>
              <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                <p>
                  We strive to ensure that our emails are relevant, timely, and
                  not excessive in frequency. Our emails will:
                </p>
                <ul>
                  <li>Clearly identify Numberz Insight as the sender</li>
                  <li>
                    Include our business contact information in the footer
                  </li>
                  <li>
                    Contain a clear subject line that accurately reflects the
                    content
                  </li>
                  <li>Include a functional unsubscribe mechanism</li>
                  <li>
                    Be sent at reasonable frequencies based on the service
                    context
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <FileText className="h-5 w-5 mr-2 text-primary" />
                  Prohibited Activities
                </CardTitle>
              </CardHeader>
              <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                <p>You agree not to:</p>
                <ul>
                  <li>Forward our emails to others without permission</li>
                  <li>Use our email communications for any unlawful purpose</li>
                  <li>
                    Attempt to reverse engineer our email systems or tracking
                    mechanisms
                  </li>
                  <li>
                    Use automated systems to interact with our emails unless
                    expressly authorized
                  </li>
                </ul>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="security" className="space-y-6">
            <Alert className="bg-primary/5 border-primary/20">
              <Shield className="h-4 w-4 text-primary" />
              <AlertTitle>Security & Privacy</AlertTitle>
              <AlertDescription>
                How we protect your information in email communications.
              </AlertDescription>
            </Alert>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <Shield className="h-5 w-5 mr-2 text-primary" />
                  Security and Confidentiality
                </CardTitle>
              </CardHeader>
              <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                <p>
                  While we implement security measures to protect email
                  communications, email is not inherently secure. For highly
                  sensitive information:
                </p>
                <ul>
                  <li>
                    We may use secure links rather than including sensitive
                    content directly in emails
                  </li>
                  <li>
                    We recommend using our secure portal for uploading sensitive
                    documents
                  </li>
                  <li>
                    We advise against sending tax file numbers or financial
                    account details via email
                  </li>
                </ul>
                <p>
                  You are responsible for maintaining the security of your email
                  account and promptly notifying us of any unauthorized access.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <FileText className="h-5 w-5 mr-2 text-primary" />
                  Email Retention
                </CardTitle>
              </CardHeader>
              <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                <p>
                  We retain email communications in accordance with our Privacy
                  Policy and applicable tax and legal requirements. Generally,
                  we retain tax-related communications for 5-7 years as required
                  by Australian tax regulations.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <Settings className="h-5 w-5 mr-2 text-primary" />
                  Third-Party Email Services
                </CardTitle>
              </CardHeader>
              <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                <p>
                  We use Amazon Web Services (AWS) Simple Email Service (SES) to
                  send and manage our email communications. By accepting these
                  Terms, you acknowledge that your email address and related
                  data may be processed by AWS in accordance with their privacy
                  policies and our data processing agreements with them.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="mt-10 pt-6 border-t border-border">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="text-lg font-medium mb-3 flex items-center">
                <Mail className="h-5 w-5 mr-2 text-primary" />
                Email Commitment
              </h3>
              <p className="text-muted-foreground">
                We're committed to responsible email practices and respecting
                your communication preferences.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium mb-3 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-primary" />
                Related Documents
              </h3>
              <ul className="space-y-2">
                <li>
                  <Button
                    variant="link"
                    className="p-0 h-auto text-primary hover:underline flex items-center"
                    onClick={() =>
                      (window.location.href = "/email/privacy-policy")
                    }
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Email Privacy Policy
                  </Button>
                </li>
                <li>
                  <Button
                    variant="link"
                    className="p-0 h-auto text-primary hover:underline flex items-center"
                    onClick={() => (window.location.href = "/privacy")}
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Main Privacy Policy
                  </Button>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-medium mb-3 flex items-center">
                <Settings className="h-5 w-5 mr-2 text-primary" />
                Manage Preferences
              </h3>
              <Button
                variant="default"
                onClick={() => (window.location.href = "/email/preferences")}
              >
                Email Preferences
              </Button>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-border text-center">
            <p className="text-sm text-muted-foreground">
              Last Updated:{" "}
              {new Date().toLocaleDateString("en-AU", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </p>
          </div>
        </div>
      </div>
    </EmailPageLayout>
  );
}
